<template>
  <section class="dnc card mb-3">
    <div class="card-body">
      <div class="dnc-head">
        <div>
          <h6 class="mb-1"><CygnusIcon name="shield" class="me-1" />Do-not-contact list</h6>
          <p class="small text-muted mb-0 dnc-intro">
            People and companies this workspace must never email: existing clients, competitors, anyone who asked
            not to be contacted. Campaigns, drip flows, one-off emails and tests from this workspace skip them, even
            mail that was queued before they were added. The list applies to this workspace only.
          </p>
        </div>
        <div class="dnc-counts" v-if="summary">
          <div><span class="n">{{ summary.counts.addresses.toLocaleString() }}</span> addresses</div>
          <div><span class="n">{{ summary.counts.domains.toLocaleString() }}</span> domains</div>
        </div>
      </div>
      <p class="small text-muted mt-2 mb-0">
        Addresses are stored scrambled, so they cannot be listed here: search for one by its full address below.
        Domains are listed, and block every address on that domain and its subdomains.
      </p>

      <div v-if="error" class="alert alert-danger py-1 px-2 small mt-2 mb-0">{{ error }}</div>
      <div v-if="message" class="dnc-msg small mt-2">{{ message }}</div>

      <!-- Upload -->
      <div class="dnc-block">
        <div class="dnc-label">Upload a list</div>
        <p class="small text-muted mb-1">
          A CSV or text file, one email address or <code>@domain</code> per line (for a CSV, the first column is used).
          You will see what it adds before anything is saved.
        </p>
        <div class="d-flex gap-2 align-items-center flex-wrap">
          <input type="file" accept=".csv,.txt,text/csv,text/plain" class="form-control form-control-sm dnc-file" :disabled="busy" @change="readFile">
          <span v-if="fileName" class="small text-muted">{{ fileName }}</span>
        </div>
        <textarea v-model="text" rows="4" class="form-control form-control-sm mt-2" :disabled="busy"
                  placeholder="or paste here: name@company.com, @competitor.com"></textarea>
        <div class="d-flex gap-2 mt-2">
          <button type="button" class="btn btn-sm btn-outline-primary" :disabled="busy || !text.trim()" @click="runPreview">Check the list</button>
          <button v-if="preview" type="button" class="btn btn-sm btn-primary" :disabled="busy || preview.new === 0" @click="runImport">
            Add {{ preview.new }} {{ preview.new === 1 ? 'entry' : 'entries' }}
          </button>
          <button v-if="preview" type="button" class="btn btn-sm btn-link" :disabled="busy" @click="clearUpload">Cancel</button>
        </div>
        <div v-if="preview" class="dnc-preview small mt-2">
          {{ preview.lines }} lines: <strong>{{ preview.new }} new</strong>
          ({{ preview.new_addresses }} addresses, {{ preview.new_domains }} domains),
          {{ preview.already_present }} already on the list, {{ preview.invalid }} not valid.
          <span v-if="preview.invalid_lines.length">
            Not valid: line {{ preview.invalid_lines.join(', ') }}<span v-if="preview.invalid > preview.invalid_lines.length"> and more</span>.
          </span>
        </div>
      </div>

      <!-- Add one -->
      <div class="dnc-block">
        <div class="dnc-label">Add one</div>
        <div class="d-flex gap-2">
          <input v-model="entry" type="text" class="form-control form-control-sm dnc-input" placeholder="name@company.com or @company.com" :disabled="busy" @keyup.enter="addOne">
          <button type="button" class="btn btn-sm btn-outline-primary" :disabled="busy || !entry.trim()" @click="addOne">Add</button>
        </div>
      </div>

      <!-- Search -->
      <div class="dnc-block">
        <div class="dnc-label">Is an address on the list?</div>
        <div class="d-flex gap-2">
          <input v-model="searchAddress" type="email" class="form-control form-control-sm dnc-input" placeholder="name@company.com" :disabled="busy" @keyup.enter="runSearch">
          <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="busy || !searchAddress.trim()" @click="runSearch">Search</button>
        </div>
        <div v-if="found" class="small mt-2">
          <template v-if="found.blocked && found.entry.kind === 'address'">
            On the list as an address.
            <button type="button" class="btn btn-sm btn-link text-danger p-0 ms-1" :disabled="busy" @click="remove(found.entry.id, 'this address')">Remove it</button>
          </template>
          <template v-else-if="found.blocked">
            Blocked by the domain entry <code>@{{ found.entry.domain }}</code>.
          </template>
          <template v-else>Not on the list.</template>
        </div>
      </div>

      <!-- Domains -->
      <div class="dnc-block" v-if="summary && summary.domains.length">
        <div class="dnc-label">Domains</div>
        <ul class="dnc-domains">
          <li v-for="d in summary.domains" :key="d.id">
            <code>@{{ d.domain }}</code>
            <span class="text-muted small">{{ d.reason === 'import' ? 'uploaded' : 'added' }} {{ fmtDate(d.created) }}</span>
            <button type="button" class="btn btn-sm btn-link text-danger p-0" :disabled="busy" @click="remove(d.id, '@' + d.domain)">Remove</button>
          </li>
        </ul>
      </div>

      <div class="dnc-block small text-muted" v-if="summary && summary.removals.length">
        <div class="dnc-label">Recently removed</div>
        <div v-for="r in summary.removals.slice(0, 5)" :key="r.id + '-' + r.removed">
          {{ r.kind === 'domain' ? '@' + r.domain : 'An address' }} (entry #{{ r.id }}), {{ fmtDate(r.removed) }}
        </div>
      </div>
    </div>
  </section>
</template>

<script>
/**
 * DoNotContactPanel - the workspace's own do-not-contact list (send safety
 * slice 5), on the Compliance page. Reads and writes
 * /api/v1/admin/compliance/do-not-contact (Ops::COMPLIANCE).
 */
import useApi from "@apexl/icarus-core/src/services/api";

const BASE = '/api/v1/admin/compliance/do-not-contact';

export default {
  name: 'DoNotContactPanel',
  data() {
    return {
      api: useApi(),
      summary: null,
      busy: false,
      error: '',
      message: '',
      text: '',
      fileName: '',
      preview: null,
      entry: '',
      searchAddress: '',
      found: null,
    };
  },
  mounted() { this.load(); },
  methods: {
    body(res) { return res?.data ?? res; },
    fail(e, fallback) { this.error = e?.response?.data?.error || e?.message || fallback; },
    fmtDate(ts) { return ts ? new Date(ts * 1000).toLocaleDateString() : ''; },
    async load() {
      try {
        this.summary = this.body(await this.api.get(BASE));
      } catch (e) { this.fail(e, 'Could not load the list.'); }
    },
    readFile(ev) {
      const file = ev?.target?.files?.[0];
      if (!file) return;
      this.fileName = file.name;
      const reader = new FileReader();
      reader.onload = () => { this.text = String(reader.result || ''); this.preview = null; };
      reader.readAsText(file);
    },
    clearUpload() { this.text = ''; this.fileName = ''; this.preview = null; },
    async runPreview() {
      this.busy = true; this.error = ''; this.message = '';
      try {
        this.preview = this.body(await this.api.post(`${BASE}/preview`, { text: this.text }))?.preview || null;
      } catch (e) { this.fail(e, 'Could not check the list.'); }
      this.busy = false;
    },
    async runImport() {
      this.busy = true; this.error = '';
      try {
        const res = this.body(await this.api.post(`${BASE}/import`, { text: this.text }));
        const r = res?.imported || {};
        this.message = `Added ${r.new} ${r.new === 1 ? 'entry' : 'entries'}; ${r.already_present} already on the list, ${r.invalid} not valid.`;
        this.summary = res?.summary || this.summary;
        this.clearUpload();
      } catch (e) { this.fail(e, 'Could not add the list.'); }
      this.busy = false;
    },
    async addOne() {
      this.busy = true; this.error = ''; this.message = '';
      try {
        const res = this.body(await this.api.post(BASE, { entry: this.entry }));
        this.message = res?.status === 'added' ? 'Added to the list.' : 'That is already on the list.';
        this.entry = '';
        await this.load();
      } catch (e) { this.fail(e, 'Could not add it.'); }
      this.busy = false;
    },
    async runSearch() {
      this.busy = true; this.error = ''; this.found = null;
      try {
        this.found = this.body(await this.api.post(`${BASE}/search`, { address: this.searchAddress }));
      } catch (e) { this.fail(e, 'Could not search.'); }
      this.busy = false;
    },
    async remove(id, label) {
      if (!confirm(`Remove ${label} from the do-not-contact list? This workspace will be able to email it again.`)) return;
      this.busy = true; this.error = ''; this.message = '';
      try {
        await this.api.delete(`${BASE}/${id}`);
        this.message = 'Removed from the list.';
        this.found = null;
        await this.load();
      } catch (e) { this.fail(e, 'Could not remove it.'); }
      this.busy = false;
    },
  },
};
</script>

<style>
.dnc { background: var(--m-bg-card, #FFF); }
.dnc-head { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; flex-wrap: wrap; }
.dnc-intro { max-width: 680px; }
.dnc-counts { display: flex; gap: 16px; font-size: 0.85rem; color: var(--m-text-secondary, #475569); }
.dnc-counts .n { font-size: 1.2rem; font-weight: 600; color: var(--m-text-primary, #0F172A); }
.dnc-block { border-top: 1px solid var(--m-border, #E2E8F0); margin-top: 14px; padding-top: 12px; }
.dnc-label { font-weight: 600; font-size: 0.85rem; margin-bottom: 4px; }
.dnc-file, .dnc-input { max-width: 360px; }
.dnc-domains { list-style: none; padding: 0; margin: 0; }
.dnc-domains li { display: flex; gap: 10px; align-items: center; padding: 2px 0; }
.dnc-msg { color: var(--m-status-success, #166534); }
.dnc-preview { color: var(--m-text-secondary, #475569); }
</style>
